import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import supportTicketService from '../../services/supportTicketService';
import TicketThread from '../../components/Support/TicketThread';
import TicketComposer from '../../components/Support/TicketComposer';

/**
 * AdminInbox — tenant admin's support ticket inbox.
 *
 * LAYOUT
 * ------
 * Desktop (≥768px) — three panes side by side:
 *   Left:   filter sidebar (Status / Category)
 *   Middle: ticket list (searchable)
 *   Right:  detail pane (thread + composer + status action bar)
 *
 * Mobile (<768px) — single-column drill-down:
 *   list fills the viewport → tap a ticket → thread replaces it → Back returns.
 *
 * The drill-down is not a nicety. The three panes previously carried hard pixel
 * widths (sidebar 220, list 360, detail flex-1) and the sidebar was merely
 * `hidden md:block`. On a 375px phone the list alone claimed 360px, leaving the
 * detail pane ~15px wide — so tapping a ticket LOOKED like it did nothing. The
 * thread rendered correctly the whole time, in a sliver too narrow to read.
 *
 * `mobilePane` rather than deriving the view from `selectedRef`: Back must
 * return to the list WITHOUT dropping the loaded thread, so re-opening the same
 * ticket is instant instead of refetching it every time the admin flicks
 * between list and thread.
 *
 * Visibility (server-enforced by PR 4 controller):
 *   Tenant admin sees ALL tickets raised in their tenant — across their
 *   company's users + cross-company. Credits tickets show as read-only
 *   (canAct=false) because super admin owns those.
 *
 * Route is guarded inside the component (not at the Route level) so the
 * "not authorized" hint is friendlier than a hard redirect. Mounts under
 * /app/support/inbox.
 */
/* Filter definitions — shared by the desktop sidebar and the mobile
   dropdowns so the two can never drift out of sync. */
const STATUS_FILTERS = [
  { key: 'open',        label: 'Open',        icon: 'pi-circle-fill',  color: '#f59e0b' },
  { key: 'in_progress', label: 'In Progress', icon: 'pi-spinner',      color: '#3b82f6' },
  { key: 'resolved',    label: 'Resolved',    icon: 'pi-check-circle', color: '#10b981' },
  { key: 'closed',      label: 'Closed',      icon: 'pi-times-circle', color: '#94a3b8' },
];

const CATEGORY_FILTERS = [
  { key: 'borrower_reports', label: 'Borrower Reports', icon: 'pi-file' },
  { key: 'offer_generation', label: 'Offer Generation', icon: 'pi-receipt' },
  { key: 'credits_issue',    label: 'Credits',          icon: 'pi-credit-card' },
  { key: 'others',           label: 'Others',           icon: 'pi-comment' },
];

// Clearing either mobile dropdown returns here rather than to "no filter" —
// the list is always filtered by exactly one thing, same as the sidebar.
const DEFAULT_FILTER = { kind: 'status', value: 'open' };

// Shared by FilterGroup's section titles and the mobile dropdown labels, so
// the two surfaces stay typographically identical.
const FILTER_HEADING_CLASS = 'block text-xs uppercase font-bold mb-2 px-1';
const FILTER_HEADING_STYLE = { color: 'var(--text-color-secondary)', letterSpacing: '.08em' };

// Icon + label, so the dropdown carries the same non-verbal cue as the sidebar.
const filterOptionTemplate = (option) => (
  <span className="flex align-items-center gap-2">
    <i
      className={`pi ${option.icon} text-sm`}
      style={option.color ? { color: option.color } : undefined}
      aria-hidden="true"
    />
    <span className="text-sm">{option.label}</span>
  </span>
);

const filterValueTemplate = (option, props) => (
  option ? filterOptionTemplate(option) : <span className="text-500 text-sm">{props.placeholder}</span>
);

const AdminInbox = () => {
  const toastRef = useRef(null);

  // Auth gate — simple localStorage check
  const userRoles = JSON.parse(localStorage.getItem('userRoles') || '[]');
  const isAdmin = ['tenant_admin', 'company_admin', 'company_owner', 'super_admin']
    .some((r) => userRoles.includes(r));

  // ── State ───────────────────────────────────────────────────────────
  const [tickets, setTickets] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeFilter, setActiveFilter] = useState({ kind: 'status', value: 'open' });
  const [searchText, setSearchText] = useState('');

  const [selectedRef, setSelectedRef] = useState(null);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [transitioningStatus, setTransitioningStatus] = useState(false);

  // ── Mobile drill-down ───────────────────────────────────────────────
  // 767px, so the JS breakpoint is the exact complement of the `md:` prefix
  // the sidebar uses. A mismatch here would leave a band of widths where the
  // sidebar is hidden but the panes still think they are on desktop.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => setIsMobile(mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);

  // Which pane the phone is showing. Ignored on desktop, where both render.
  const [mobilePane, setMobilePane] = useState('list');

  const openTicket = (ref) => {
    setSelectedRef(ref);
    if (isMobile) setMobilePane('detail');
  };

  // loadList() drops the selection when the open ticket falls out of the
  // active filter. On a phone that would otherwise strand the user on a
  // detail pane with nothing in it.
  useEffect(() => {
    if (!selectedRef && mobilePane === 'detail') setMobilePane('list');
  }, [selectedRef, mobilePane]);

  // ── Shell height measured from real chrome (skill P23) ──────────────
  // The panes were sized `calc(100vh - 80px)`, a guess at the topbar height
  // that ignores everything below: `.layout-footer` on desktop, and on mobile
  // the FIXED `.mobile-bottom-nav`, which overlays the last ~3.75rem of the
  // viewport. With the guess, the reply composer at the bottom of a thread sat
  // underneath the nav bar and could not be tapped. Measure instead.
  const shellRef = useRef(null);
  const [shellHeight, setShellHeight] = useState('80vh');
  useLayoutEffect(() => {
    const measure = () => {
      if (!shellRef.current) return;
      const top = shellRef.current.getBoundingClientRect().top;
      const selectors = ['.mobile-bottom-nav', '.layout-footer', '.layout-main-footer', 'footer'];
      let bottomEdge = window.innerHeight;
      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        // Skip hidden chrome. A `display:none` footer reports a 0x0 rect at
        // top 0, and treating that as the bottom edge collapses the shell to
        // its 320px floor on every desktop load.
        if (rect.height === 0 && rect.width === 0) continue;
        if (rect.top <= top) continue;
        if (rect.top < bottomEdge) bottomEdge = rect.top;
      }
      setShellHeight(`${Math.max(bottomEdge - top - 8, 320)}px`);
    };
    measure();
    // Chrome can mount a frame later than this effect; re-measure once.
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      cancelAnimationFrame(raf);
    };
  }, [isMobile, mobilePane]);

  // ── Load list ───────────────────────────────────────────────────────
  // `clearSelectionIfMissing` defaults to true. The post-status-transition
  // refresh (refreshAfterReply / transition) passes `false` so the just-
  // resolved ticket stays visible momentarily for confirmation — the user
  // explicitly leaves it by clicking a different ticket or filter.
  const loadList = useCallback(async (clearSelectionIfMissing = true) => {
    if (!isAdmin) {
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    try {
      const params = {};
      if (activeFilter.kind === 'status') {
        params.status = activeFilter.value;
      } else if (activeFilter.kind === 'category') {
        params.category = activeFilter.value;
      }
      // 'q' is debounced via the search input's onBlur; for now pass as-is
      if (searchText.trim()) params.q = searchText.trim();

      const res = await supportTicketService.listTickets(params);
      setTickets(res.data);

      // If the currently-selected ticket isn't in the new list (because
      // the user just switched filters), clear the right pane so we don't
      // show a "ghost" ticket that doesn't match the active filter.
      if (clearSelectionIfMissing) {
        setSelectedRef((prevRef) => {
          if (!prevRef) return prevRef;
          const stillVisible = res.data.some((t) => t.ticket_ref === prevRef);
          return stillVisible ? prevRef : null;
        });
      }
    } catch (e) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Could not load tickets',
        detail: e?.response?.data?.message || 'Try refreshing.',
        life: 4000,
      });
    } finally {
      setLoadingList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, isAdmin]);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Load thread ─────────────────────────────────────────────────────
  const loadThread = useCallback(async (ref) => {
    if (!ref) return;
    setLoadingThread(true);
    try {
      const data = await supportTicketService.getTicket(ref);
      setThread(data);
    } catch (e) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Could not load ticket',
        detail: e?.response?.data?.message || 'Try again.',
        life: 4000,
      });
    } finally {
      setLoadingThread(false);
    }
  }, []);

  useEffect(() => {
    if (selectedRef) {
      loadThread(selectedRef);
    } else {
      // Selection cleared (e.g. ticket moved out of the active filter) —
      // drop the stale thread so the detail pane renders the "Pick a ticket"
      // empty state instead of the previous ticket's content.
      setThread(null);
    }
  }, [selectedRef, loadThread]);

  const refreshAfterReply = () => {
    // Reload both — thread for the new message, list for last_reply_at sort.
    // Pass false so the selected ticket stays visible even if its status
    // change moved it out of the active filter (admin wants to see the
    // confirmation, not have it vanish).
    loadThread(selectedRef);
    loadList(false);
  };

  // ── Status transitions ──────────────────────────────────────────────
  const transition = async (status, confirmMsg) => {
    if (!selectedRef || transitioningStatus) return;
    const doIt = async () => {
      setTransitioningStatus(true);
      try {
        await supportTicketService.transitionStatus(selectedRef, status);
        toastRef.current?.show({
          severity: 'success',
          summary: `Marked ${humanizeStatus(status)}`,
          life: 2500,
        });
        await loadThread(selectedRef);
        // Pass false so the just-transitioned ticket stays visible on
        // the right side after the action — admin sees confirmation
        // before the ticket disappears from the active filter.
        await loadList(false);
      } catch (e) {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Status change failed',
          detail: e?.response?.data?.message || 'Try again.',
          life: 4000,
        });
      } finally {
        setTransitioningStatus(false);
      }
    };
    if (confirmMsg) {
      confirmDialog({
        message: confirmMsg,
        header: 'Confirm',
        icon: 'pi pi-exclamation-triangle',
        accept: doIt,
      });
    } else {
      doIt();
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  if (!isAdmin) {
    return <NotAuthorized />;
  }

  // Desktop shows every pane at once; a phone shows exactly one.
  //
  // `|| !selectedRef` on the list, and `&& selectedRef` on the detail, so that
  // the frame between the selection clearing and the effect above resetting
  // `mobilePane` shows the list rather than an empty detail pane.
  const showList = !isMobile || mobilePane === 'list' || !selectedRef;
  const showDetail = !isMobile || (mobilePane === 'detail' && !!selectedRef);

  return (
    <div ref={shellRef} className="flex" style={{ height: shellHeight }}>
      <Toast ref={toastRef} position="top-right" />
      <ConfirmDialog />

      {/* ── LEFT: filter sidebar ──────────────────────────────────── */}
      {/* `lg`, not `md`. At exactly 768px a 224px sidebar plus the 368px list
          left the thread 176px — the same unreadable sliver this issue is
          about, simply at a different width. Below 992px the filters live as
          dropdowns in the list header instead, which costs nothing: they are
          the same STATUS_FILTERS / CATEGORY_FILTERS arrays. */}
      <aside
        className="hidden lg:block surface-card border-right-1 surface-border p-3 flex-shrink-0 overflow-y-auto w-14rem"
      >
        <FilterGroup
          title="Status"
          items={STATUS_FILTERS}
          activeFilter={activeFilter}
          kind="status"
          onPick={(k) => setActiveFilter({ kind: 'status', value: k })}
        />

        <FilterGroup
          title="Category"
          items={CATEGORY_FILTERS}
          activeFilter={activeFilter}
          kind="category"
          onPick={(k) => setActiveFilter({ kind: 'category', value: k })}
        />
      </aside>

      {/* ── MIDDLE: ticket list ───────────────────────────────────── */}
      {/* Full width on a phone, fixed column on desktop. The old fixed 360px
          applied at EVERY width — that is what squeezed the detail pane to a
          15px sliver. Width is inline because PrimeFlex ships `w-23rem` but no
          responsive `md:w-23rem` variant; it is driven by state, which is the
          one case the design system allows inline style for. */}
      <div
        className={`${showList ? 'flex' : 'hidden'} surface-card border-right-1 surface-border flex-column flex-shrink-0 h-full`}
        style={{ width: isMobile ? '100%' : '23rem', minHeight: 0 }}
      >
        <div
          className="p-3 border-bottom-1 surface-border surface-card flex align-items-center gap-2"
          style={{ position: 'sticky', top: 0, zIndex: 1 }}
        >
          <h3 className="m-0 text-sm font-bold text-color flex-1">
            {humanizeFilterTitle(activeFilter)}
          </h3>
          <span
            className="text-xs px-2 border-round-pill font-bold"
            style={{ background: 'var(--surface-100)', color: 'var(--text-color-secondary)' }}
          >
            {tickets.length}
          </span>
        </div>

        {/* Compact filters — shown wherever the sidebar isn't, which is now
            everything below `lg` (see the aside's comment). Without these,
            Status and Category would be unreachable and the list stuck on the
            default "Open". Exactly ONE filter is active at a time (same as the
            sidebar's radio behaviour), so choosing in one dropdown clears the
            other, and clearing either falls back to the default rather than to
            an unfiltered list the API can't represent. */}
        <div className="block lg:hidden p-3 border-bottom-1 surface-border surface-card">
          <div className="grid">
            <div className="col-6">
              {/* Heading styling mirrors FilterGroup's section titles so the
                  mobile filters read the same as the desktop sidebar. */}
              <label htmlFor="inbox-status-filter" className={FILTER_HEADING_CLASS} style={FILTER_HEADING_STYLE}>
                Status
              </label>
              <Dropdown
                inputId="inbox-status-filter"
                value={activeFilter.kind === 'status' ? activeFilter.value : null}
                options={STATUS_FILTERS}
                optionLabel="label"
                optionValue="key"
                onChange={(e) => setActiveFilter(e.value ? { kind: 'status', value: e.value } : DEFAULT_FILTER)}
                itemTemplate={filterOptionTemplate}
                valueTemplate={filterValueTemplate}
                placeholder="Status"
                showClear
                className="w-full p-inputtext-sm"
              />
            </div>
            <div className="col-6">
              <label htmlFor="inbox-category-filter" className={FILTER_HEADING_CLASS} style={FILTER_HEADING_STYLE}>
                Category
              </label>
              <Dropdown
                inputId="inbox-category-filter"
                value={activeFilter.kind === 'category' ? activeFilter.value : null}
                options={CATEGORY_FILTERS}
                optionLabel="label"
                optionValue="key"
                onChange={(e) => setActiveFilter(e.value ? { kind: 'category', value: e.value } : DEFAULT_FILTER)}
                itemTemplate={filterOptionTemplate}
                valueTemplate={filterValueTemplate}
                placeholder="Category"
                showClear
                className="w-full p-inputtext-sm"
              />
            </div>
          </div>
        </div>

        <div className="p-3 border-bottom-1 surface-border surface-card">
          {/* IconField, not the deprecated `p-input-icon-left` wrapper (P1) —
              in PrimeReact 10 that wrapper's left-padding rule no longer
              applies reliably, so the magnifier overlapped the placeholder. */}
          <IconField iconPosition="left" className="w-full">
            <InputIcon className="pi pi-search" />
            <InputText
              placeholder="Search ref or note…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onBlur={() => loadList()}
              onKeyDown={(e) => { if (e.key === 'Enter') loadList(); }}
              className="w-full p-inputtext-sm"
            />
          </IconField>
        </div>

        {/* min-height:0 — without it this flex child refuses to shrink below
            its content height, the inner scrollbar never engages, and the list
            pushes the whole page into a second scrollbar instead. */}
        <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
          {loadingList ? (
            <ListSkeleton />
          ) : tickets.length === 0 ? (
            <EmptyList />
          ) : (
            tickets.map((t) => (
              <TicketRow
                key={t.ticket_ref}
                ticket={t}
                active={t.ticket_ref === selectedRef}
                onClick={() => openTicket(t.ticket_ref)}
              />
            ))
          )}
        </div>
      </div>

      {/* ── RIGHT: detail pane ────────────────────────────────────── */}
      <main
        className={`${showDetail ? 'block' : 'hidden'} flex-1 surface-ground overflow-y-auto h-full`}
        style={{ minWidth: 0, minHeight: 0 }}
      >
        {!selectedRef ? (
          <NoTicketSelected />
        ) : loadingThread || !thread ? (
          <div className="p-4"><ThreadSkeleton /></div>
        ) : (
          <DetailPane
            thread={thread}
            transitioning={transitioningStatus}
            onTransition={transition}
            onReplied={refreshAfterReply}
            toastRef={toastRef}
            outOfView={!tickets.some((t) => t.ticket_ref === thread.ticket_ref)}
            activeFilter={activeFilter}
            // Desktop keeps the list permanently on screen, so Back would be
            // meaningless there. null hides it.
            onBack={isMobile ? () => setMobilePane('list') : null}
          />
        )}
      </main>
    </div>
  );
};

// ── Subcomponents ────────────────────────────────────────────────────

const FilterGroup = ({ title, items, activeFilter, kind, onPick }) => (
  <div className="mb-3">
    <div className={FILTER_HEADING_CLASS} style={FILTER_HEADING_STYLE}>
      {title}
    </div>
    <div className="flex flex-column gap-1">
      {items.map((it) => {
        const active = activeFilter.kind === kind && activeFilter.value === it.key;
        return (
          <button
            key={it.key}
            onClick={() => onPick(it.key)}
            className="flex align-items-center gap-2 px-2 py-2 border-none border-round cursor-pointer text-sm"
            style={{
              background: active ? 'var(--primary-50)' : 'transparent',
              color: active ? 'var(--primary-700)' : 'var(--text-color)',
              fontWeight: active ? 700 : 500,
              textAlign: 'left',
              fontSize: '.85rem',
            }}
          >
            <i
              className={`pi ${it.icon}`}
              style={{ fontSize: '.8rem', color: it.color || 'inherit' }}
              aria-hidden="true"
            ></i>
            {it.label}
          </button>
        );
      })}
    </div>
  </div>
);

const TicketRow = ({ ticket, active, onClick }) => (
  <button
    onClick={onClick}
    className="block border-none border-bottom-1 surface-border w-full p-3 cursor-pointer text-left"
    style={{
      background: active ? 'var(--primary-50)' : '#fff',
      boxShadow: active ? 'inset 3px 0 0 var(--primary-color)' : 'none',
    }}
  >
    <div className="flex align-items-center gap-2 mb-1 flex-wrap">
      <span
        className="font-mono text-xs font-bold"
        style={{ color: 'var(--primary-700)' }}
      >
        {ticket.ticket_ref}
      </span>
      <Tag
        value={humanizeCategory(ticket.category)}
        severity="info"
        rounded
        style={{ fontSize: '.6rem', padding: '.1rem .4rem' }}
      />
    </div>

    <div
      className="text-sm font-bold text-color mb-1"
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
      }}
    >
      {ticket.note_preview || '(no note)'}
    </div>

    <div className="flex align-items-center gap-2 text-xs text-color-secondary flex-wrap">
      <i className="pi pi-user" style={{ fontSize: '.65rem' }} aria-hidden="true"></i>
      <span>{ticket.raised_by?.name || 'Unknown'}</span>
      <StatusTag status={ticket.status} small />
      <span className="ml-auto">{formatRelative(ticket.last_reply_at || ticket.created_at)}</span>
    </div>
  </button>
);

const StatusTag = ({ status, small }) => {
  const map = {
    open:        { value: 'Open',        severity: 'warning' },
    in_progress: { value: 'In Progress', severity: 'info' },
    resolved:    { value: 'Resolved',    severity: 'success' },
    closed:      { value: 'Closed',      severity: 'secondary' },
    reopened:    { value: 'Reopened',    severity: 'danger' },
  };
  const m = map[status] || { value: status, severity: 'info' };
  return (
    <Tag
      value={m.value}
      severity={m.severity}
      rounded
      style={small ? { fontSize: '.6rem', padding: '.1rem .4rem' } : undefined}
    />
  );
};

const DetailPane = ({ thread, transitioning, onTransition, onReplied, toastRef, outOfView, activeFilter, onBack }) => {
  const canAct = !!thread.company_admin_can_act;
  const status = thread.status;

  return (
    <div className="p-3 md:p-4">
      {/* Header with back (mobile only) + status actions.
          Sticky so the way out of the thread stays reachable while reading a
          long one — on a phone this is the ONLY route back to the list. */}
      <div
        className="flex align-items-center justify-content-between gap-2 mb-3 flex-wrap surface-ground pt-1 pb-2"
        style={{ position: 'sticky', top: 0, zIndex: 1 }}
      >
        <div className="flex align-items-center gap-2">
          {onBack && (
            <Button
              icon="pi pi-arrow-left"
              text
              rounded
              onClick={onBack}
              aria-label="Back to ticket list"
              // 2.75rem = 44px, the minimum tap target. PrimeReact's `small`
              // text button renders under that.
              style={{ width: '2.75rem', height: '2.75rem' }}
            />
          )}
          <span className="font-mono text-sm font-bold" style={{ color: 'var(--primary-700)' }}>
            {thread.ticket_ref}
          </span>
        </div>
        <StatusActionBar
          status={status}
          canAct={canAct}
          transitioning={transitioning}
          onTransition={onTransition}
        />
      </div>

      {/* Out-of-view banner: shown when the open ticket is no longer in
          the current filter (typically just after a status transition).
          Tells the admin why the list doesn't show it.

          A PrimeReact <Message>, not a hand-rolled div — the previous version
          hardcoded three hex colours for a banner the component already
          renders from theme variables. Wording avoids "on the left": below
          768px the filters are dropdowns above the list, not a sidebar. */}
      {outOfView && (
        <Message
          severity="info"
          className="w-full mb-3 justify-content-start"
          content={
            <span className="text-sm">
              <i className="pi pi-info-circle mr-2" aria-hidden="true"></i>
              This ticket is now <strong>{humanizeStatus(status)}</strong> — it isn&apos;t in
              your current <strong>{humanizeFilterTitle(activeFilter)}</strong> view. Change
              the filter to find it again later.
            </span>
          }
        />
      )}

      {/* Thread */}
      <TicketThread ticket={thread} />

      {/* Composer (hidden when closed) */}
      <div className="mt-3">
        {status === 'closed' ? (
          <div
            className="p-3 border-round text-center text-sm text-color-secondary"
            style={{ background: 'var(--surface-100)' }}
          >
            This ticket is closed. Reopen it to add a reply.
          </div>
        ) : (
          <TicketComposer
            ticketRef={thread.ticket_ref}
            canAct={canAct}
            onReplied={onReplied}
            toastRef={toastRef}
          />
        )}
      </div>
    </div>
  );
};

const StatusActionBar = ({ status, canAct, transitioning, onTransition }) => {
  // Allowed transitions match the backend state machine. We render only
  // the actions that make sense for the current status + the user's
  // ability to act on it. Credits tickets (canAct=false) hide status
  // actions entirely — super admin handles those.
  if (!canAct) {
    return (
      <div className="text-xs text-color-secondary">
        <i className="pi pi-info-circle mr-1" aria-hidden="true"></i>
        Credits ticket — only super admin can change status.
      </div>
    );
  }

  const actions = [];
  if (status === 'open' || status === 'reopened') {
    actions.push({ label: "I'm on it", icon: 'pi-spinner', target: 'in_progress', severity: 'info' });
    actions.push({ label: 'Resolve',  icon: 'pi-check',   target: 'resolved',    severity: 'success' });
  }
  if (status === 'in_progress') {
    actions.push({ label: 'Resolve', icon: 'pi-check', target: 'resolved', severity: 'success' });
    actions.push({ label: 'Hand back to Open', icon: 'pi-undo', target: 'open', severity: 'secondary' });
  }
  if (status === 'resolved') {
    actions.push({ label: 'Close',   icon: 'pi-lock', target: 'closed',   severity: 'secondary', confirm: 'Closing is final. Reporter must raise a new ticket for the same issue. Continue?' });
    actions.push({ label: 'Reopen',  icon: 'pi-replay', target: 'reopened', severity: 'warning' });
  }

  return (
    <div className="flex gap-2 flex-wrap">
      {actions.map((a) => (
        <Button
          key={a.target}
          label={a.label}
          icon={`pi ${a.icon}`}
          onClick={() => onTransition(a.target, a.confirm)}
          disabled={transitioning}
          className={`p-button-${a.severity}`}
          style={{ fontSize: '.78rem', fontWeight: 700 }}
        />
      ))}
    </div>
  );
};

const ListSkeleton = () => (
  <div className="p-3 flex flex-column gap-2">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex flex-column gap-2">
        <Skeleton height=".8rem" width="40%" />
        <Skeleton height="1rem" />
        <Skeleton height=".8rem" width="60%" />
      </div>
    ))}
  </div>
);

const ThreadSkeleton = () => (
  <div className="flex flex-column gap-3">
    <Skeleton height="2rem" width="40%" />
    <Skeleton height="6rem" />
    <Skeleton height="4rem" />
  </div>
);

const EmptyList = () => (
  <div className="p-4 text-center text-color-secondary text-sm">
    <i
      className="pi pi-inbox block mb-2"
      // pi-2x is used 141x across this app but is defined nowhere: PrimeIcons 7
      // dropped the pi-Nx scale and no project stylesheet reinstates it. Until
      // that is fixed app-wide, an explicit size is the only thing that renders.
      style={{ fontSize: '2rem', opacity: 0.5 }}
      aria-hidden="true"
    ></i>
    No tickets in this view.
  </div>
);

const NoTicketSelected = () => (
  <div
    className="flex flex-column align-items-center justify-content-center text-color-secondary p-4"
    style={{ minHeight: '60vh' }}
  >
    <i
      className="pi pi-comments mb-3"
      style={{ fontSize: '3rem', opacity: 0.4 }}
      aria-hidden="true"
    ></i>
    <h3 className="text-color m-0 mb-1 text-lg">Pick a ticket from the list</h3>
    <p className="text-sm m-0">Choose a ticket from the list to view its thread.</p>
  </div>
);

const NotAuthorized = () => (
  <div
    className="flex flex-column align-items-center justify-content-center text-center p-4"
    style={{ minHeight: '60vh' }}
  >
    <i
      className="pi pi-lock mb-3"
      style={{ fontSize: '3rem', color: 'var(--text-color-secondary)', opacity: 0.4 }}
      aria-hidden="true"
    ></i>
    <h2 className="text-color m-0 mb-2">Admin only</h2>
    <p className="text-color-secondary max-w-30rem">
      The support inbox is available to tenant administrators. If you raised
      a ticket, you can track it under <strong>My Issues</strong>.
    </p>
  </div>
);

// ── Helpers ──────────────────────────────────────────────────────────

const humanizeCategory = (key) => ({
  borrower_reports: 'Borrower Reports',
  offer_generation: 'Offer Generation',
  credits_issue:    'Credits',
  others:           'Others',
}[key] || key);

const humanizeStatus = (key) => ({
  open:        'Open',
  in_progress: 'In Progress',
  resolved:    'Resolved',
  closed:      'Closed',
  reopened:    'Reopened',
}[key] || key);

const humanizeFilterTitle = (filter) => {
  if (filter.kind === 'status') return `${humanizeStatus(filter.value)} tickets`;
  if (filter.kind === 'category') return `${humanizeCategory(filter.value)} tickets`;
  return 'Tickets';
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso);
  const now = new Date();
  const ms = now - then;
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} d`;
  return then.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

export default AdminInbox;
